import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { useCart } from '@/Application/Selectors/cart/useCart';
import { useBrazeCommerceToolsId } from '@/Infrastructure/Utils/analytics/braze/useBrazeCommerceToolsId';
import { useBrazeDeviceAlias } from '@/Infrastructure/Utils/analytics/braze/useBrazeDeviceAlias';
import { useBrazeUserSync } from '@/Infrastructure/Utils/analytics/braze/useBrazeUserSync';
import { getCustomer } from '@/modules/customer/application/getCustomer';
import type { Customer } from '@/modules/customer/domain/Customer';
import type { CustomerRepository } from '@/modules/customer/domain/CustomerRepository';
import { setCurrentUser } from '@/modules/perseo-billing/store/user/user.reducer';

interface Props extends PropsWithChildren {
  repository: CustomerRepository;
}

interface ProviderValues {
  repository: Props['repository'];
  customer: {
    data: Customer | null;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
  };
  setData: (customer: Customer | null) => void;
}

const CustomerContext = createContext<ProviderValues>({} as ProviderValues);

const CustomerProvider: FC<Props> = ({ repository, children }) => {
  const { data: cart } = useCart();
  const paymentSuccess = useAppSelector(
    (state) => state?.billing?.paymentSuccess
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Customer | null>(null);

  const getCustomerData = useCallback(async () => {
    setError(null);
    try {
      if (paymentSuccess?.customerId) {
        const customer = await getCustomer(repository);
        setData(customer);
        setCurrentUser(customer);
        setIsLoading(false);
      } else if (cart?.id && cart?.customerId) {
        const customer = await getCustomer(repository);
        setData(customer);
        setCurrentUser(customer);
        setIsLoading(false);
      }
      if (cart?.anonymousId) {
        setCurrentUser(null);
        setIsLoading(false);
      }
    } catch (e) {
      setError((e as Error).message);
      setIsLoading(false);
    }
  }, [
    cart?.anonymousId,
    cart?.id,
    cart?.customerId,
    repository,
    paymentSuccess
  ]);

  useEffect(() => {
    getCustomerData();
  }, [getCustomerData]);

  useBrazeCommerceToolsId(data);
  useBrazeDeviceAlias(!!data?.id);

  useBrazeUserSync(
    data?.email ?? cart?.shippingAddress?.email ?? null,
    data?.addresses?.[0]?.phone ?? null
  );

  return (
    <CustomerContext.Provider
      value={{
        repository,
        customer: { data, isLoading, error, refetch: getCustomerData },
        setData
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomerContext = () => useContext(CustomerContext);

export default CustomerProvider;
